"use client"

import { useState, useRef, useCallback, type MouseEvent, type TouchEvent, type WheelEvent } from "react"

export function useCarousel(initialSlide = 0, totalSlides: number) {
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const lastMoveTimeRef = useRef(0)
  const lastWheelTimeRef = useRef(0)
  const dragStartXRef = useRef(0)
  const dragOffsetRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  // Function to go to next or previous slide with debounce
  const changeSlide = useCallback(
    (direction: 1 | -1) => {
      const now = Date.now()
      // Debounce slide changes to prevent rapid scrolling
      if (now - lastMoveTimeRef.current < 300) {
        return
      }

      lastMoveTimeRef.current = now

      const nextSlide = currentSlide + direction
      if (nextSlide >= 0 && nextSlide < totalSlides) {
        setCurrentSlide(nextSlide)
      }
    },
    [currentSlide, totalSlides],
  )

  const handleDragStart = useCallback((clientX: number) => {
    setIsDragging(true)
    dragStartXRef.current = clientX
    dragOffsetRef.current = 0
    setDragOffset(0)
  }, [])

  const handleDragMove = useCallback(
    (clientX: number) => {
      if (!isDragging) return
      dragOffsetRef.current = clientX - dragStartXRef.current
      if (rafRef.current !== null) return
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null
        setDragOffset(dragOffsetRef.current)
      })
    },
    [isDragging],
  )

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return

    const threshold = 20 // Minimum drag distance to change slide

    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && currentSlide > 0) {
        // Dragged right, go to previous slide
        setCurrentSlide(currentSlide - 1)
      } else if (dragOffset < 0 && currentSlide < totalSlides - 1) {
        // Dragged left, go to next slide
        setCurrentSlide(currentSlide + 1)
      }
    }

    setIsDragging(false)
    setDragOffset(0)
    dragOffsetRef.current = 0
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [isDragging, dragOffset, currentSlide, totalSlides])

  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      handleDragStart(e.touches[0].clientX)
    },
    [handleDragStart],
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      handleDragMove(e.touches[0].clientX)
    },
    [handleDragMove],
  )

  const handleTouchEnd = useCallback(() => {
    handleDragEnd()
  }, [handleDragEnd])

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      handleDragStart(e.clientX)
    },
    [handleDragStart],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      handleDragMove(e.clientX)
    },
    [handleDragMove],
  )

  const handleMouseUp = useCallback(() => {
    handleDragEnd()
  }, [handleDragEnd])

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      handleDragEnd()
    }
  }, [isDragging, handleDragEnd])

  const handleWheel = useCallback(
    (e: WheelEvent<HTMLDivElement>) => {
      // Debounce wheel events to prevent too rapid scrolling
      const now = Date.now()
      if (now - lastWheelTimeRef.current < 300) {
        return
      }

      lastWheelTimeRef.current = now

      const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY)
      const delta = isHorizontal ? e.deltaX : e.shiftKey ? e.deltaY : 0

      if (delta !== 0) {
        e.preventDefault()
        if (delta > 20 && currentSlide < totalSlides - 1) {
          changeSlide(1)
        } else if (delta < -20 && currentSlide > 0) {
          changeSlide(-1)
        }
      }
    },
    [currentSlide, totalSlides, changeSlide],
  )

  return {
    currentSlide,
    setCurrentSlide,
    isDragging,
    dragOffset,
    carouselRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
  }
}
